import React from "react";
import { useNavigate, Link } from "react-router-dom";
// Added FaFacebookF to the imports!
import { FaInstagram, FaLinkedinIn, FaFacebookF, FaEnvelope } from "react-icons/fa";

// ═══════════════════════════════════════════════════════════════
// SHARED STYLES (Matching HealHub UI Toolkit)
// ═══════════════════════════════════════════════════════════════
const css = `
  @import url('https://fonts.cdnfonts.com/css/satoshi');
  @import url('https://fonts.cdnfonts.com/css/eina-01');
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;0,9..144,900;1,9..144,400&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Great+Vibes&display=swap');

  :root {
    --bg:#F9F4F0;--card:#FFFFFF;
    --brand:#5A6993;--brand-light:#A1B0D8;--brand-wash:rgba(90,105,147,.06);
    --sage:#7B8F8A;--sage-wash:rgba(123,143,138,.08);
    --peach:#E6A184;--peach-wash:rgba(230,161,132,.08);
    --ink:#2D364D;--muted:#9C8474;--sand:#D6D2C4;--sand-light:#EBE7DF;--cloud:#F4F7F9;
    --font:'Satoshi','Eina 01','Plus Jakarta Sans',sans-serif;
    --font-display:'Fraunces',serif;
    --font-script:'Great Vibes',cursive;
    --r-md:14px;--r-lg:24px;--r-xl:32px;--r-full:9999px;
    --ease-spring:.4s cubic-bezier(.22,.68,0,1.1);
    --sh-lg:0 12px 40px rgba(45,54,77,.06);
    --transition:all .3s ease;
  }
  .hh-dark {
    --bg:#151820;--card:#1e2230;
    --brand:#8ea4c8;--brand-light:#6b7ea8;--brand-wash:rgba(142,164,200,.1);
    --sage:#8faaa3;--sage-wash:rgba(143,170,163,.1);
    --peach:#e8b09a;--peach-wash:rgba(232,176,154,.08);
    --ink:#e0ddd8;--muted:#9a9088;--sand:#4a4640;--sand-light:#2e2c28;--cloud:#1c2030;
    --sh-lg:0 12px 40px rgba(0,0,0,.3);
  }
  .hh-root * { box-sizing:border-box; margin:0; padding:0; }
  .hh-root {
    font-family:var(--font); min-height:100vh;
    background:var(--bg); color:var(--ink);
    position:relative; overflow-x:hidden;
    display:flex; flex-direction:column;
  }
  .hh-root::before {
    content:''; position:fixed; inset:0; z-index:0; pointer-events:none;
    background:radial-gradient(circle at 15% 5%,var(--brand-wash) 0%,transparent 50%),
               radial-gradient(circle at 85% 95%,var(--peach-wash) 0%,transparent 50%);
  }

  .hh-main { flex: 1 0 auto; padding-bottom: 60px; position: relative; z-index: 1; }

  /* Navigation Back Button - Fixed Spacing Flow */
  .hh-top-nav {
    width: 100%; max-width: 900px; margin: 0 auto;
    padding: 32px 24px 0; display: flex; align-items: center;
  }
  .hh-back-btn {
    all: unset; display: inline-flex; align-items: center; gap: 8px;
    font-size: 0.95rem; font-weight: 700; color: var(--muted);
    cursor: pointer; transition: color 0.2s ease, transform 0.2s ease;
  }
  .hh-back-btn:hover { color: var(--brand); transform: translateX(-4px); }

  /* Container & Typography */
  .hh-container {
    max-width: 900px; margin: 0 auto; padding: 20px 24px 20px;
  }
  
  /* PURE CSS HERO REDESIGN */
  .hh-hero { 
    text-align: center; 
    margin-bottom: 60px; 
    padding-top: 20px;
    animation: fadeUp .6s var(--ease-spring) both; 
  }
  .hh-hero-badge {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--card); border: 1px solid var(--sand-light);
    padding: 8px 16px; border-radius: var(--r-full);
    font-size: 0.85rem; font-weight: 700; color: var(--brand);
    text-transform: uppercase; letter-spacing: 0.08em;
    margin-bottom: 24px; box-shadow: var(--sh-lg);
  }
  .hh-title { 
    font-family: var(--font-display); 
    font-size: clamp(2.8rem, 6vw, 4rem); 
    font-weight: 800; 
    color: var(--ink); 
    letter-spacing: -0.03em; 
    line-height: 1.1; 
    margin-bottom: 12px; 
  }
  .hh-title-highlight {
    background: linear-gradient(135deg, var(--brand), var(--peach));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .hh-tagline { 
    font-family: var(--font-script); 
    font-size: clamp(1.8rem, 3.5vw, 2.4rem); 
    color: var(--muted); 
    opacity: 0.9; 
  }

  /* Text Blocks */
  .hh-text-block { 
    font-size: 1.05rem; line-height: 1.8; color: var(--ink); margin-bottom: 40px; 
    animation: fadeUp .6s var(--ease-spring) .1s both;
  }
  .hh-text-block p { margin-bottom: 20px; }
  .hh-text-block strong { color: var(--brand); font-weight: 700; }
  
  .hh-highlight-box {
    background: var(--card); border: 1px solid var(--sand-light);
    border-radius: var(--r-lg); padding: 32px 40px;
    box-shadow: var(--sh-lg); margin: 40px 0; text-align: center;
    animation: fadeUp .6s var(--ease-spring) .2s both;
  }
  .hh-highlight-box h3 { font-family: var(--font-display); font-size: 1.6rem; color: var(--brand); margin-bottom: 12px; }
  .hh-highlight-box p { font-size: 1.1rem; color: var(--ink); margin: 0; }

  /* Mission & Vision Split */
  .hh-split { display: flex; gap: 24px; margin-bottom: 60px; animation: fadeUp .6s var(--ease-spring) .3s both; }
  @media(max-width: 640px) { .hh-split { flex-direction: column; } }
  .hh-split-card {
    flex: 1; background: var(--brand-wash); border-radius: var(--r-lg); padding: 32px;
    border: 1px solid rgba(90,105,147,.1);
  }
  .hh-split-card.vision { background: var(--peach-wash); border-color: rgba(230,161,132,.1); }
  .hh-split-card h3 { font-family: var(--font-display); font-size: 1.4rem; color: var(--ink); margin-bottom: 12px; display: flex; alignItems: center; gap: 8px; }
  .hh-split-card p { font-size: 0.95rem; line-height: 1.6; color: var(--ink); margin: 0; }

  /* Feature Grid */
  .hh-section-title { font-family: var(--font-display); font-size: 2.2rem; color: var(--ink); text-align: center; margin-bottom: 40px; animation: fadeUp .6s var(--ease-spring) .4s both;}
  .hh-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(290px, 1fr)); gap: 20px;
    margin-bottom: 60px; animation: fadeUp .6s var(--ease-spring) .5s both;
  }
  .hh-feature-card {
    background: var(--card); border: 1px solid var(--sand-light);
    border-radius: var(--r-lg); padding: 28px;
    box-shadow: 0 4px 12px rgba(45,54,77,.03);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .hh-feature-card:hover { transform: translateY(-4px); box-shadow: var(--sh-lg); }
  .hh-feature-icon { font-size: 2.2rem; margin-bottom: 16px; }
  .hh-feature-title { font-size: 1.1rem; font-weight: 700; color: var(--brand); margin-bottom: 10px; }
  .hh-feature-desc { font-size: 0.9rem; line-height: 1.6; color: var(--muted); margin: 0; }

  /* Our Story */
  .hh-origin-story {
    background: var(--card); border: 2px solid var(--border-subtle, #E5E7EB);
    border-radius: var(--r-xl); padding: 40px; margin-bottom: 60px;
    position: relative; animation: fadeUp .6s var(--ease-spring) .6s both;
  }
  .hh-origin-story h2 { font-family: var(--font-display); font-size: 2.2rem; color: var(--peach); margin-bottom: 24px; text-align: center;}
  .hh-origin-text-en { font-size: 1.05rem; line-height: 1.8; color: var(--ink); margin-bottom: 28px; padding-bottom: 24px; border-bottom: 1px solid var(--sand-light);}
  
  .hh-origin-hindi {
    font-size: 1.1rem; line-height: 1.9; color: var(--ink); font-weight: 500;
    font-style: italic; background: var(--bg-secondary, #F9FAFB);
    padding: 24px; border-radius: var(--r-md); border-left: 4px solid var(--peach);
  }
  .hh-origin-hindi strong { color: var(--peach); font-weight: 700; }

  /* Security & Warning */
  .hh-disclaimers-row { display: flex; gap: 20px; margin-bottom: 60px; animation: fadeUp .6s var(--ease-spring) .7s both;}
  @media(max-width: 768px) { .hh-disclaimers-row { flex-direction: column; } }

  .hh-security {
    flex: 2; background: var(--sage-wash); border-left: 4px solid var(--sage);
    border-radius: 0 var(--r-md) var(--r-md) 0; padding: 24px 32px;
  }
  .hh-security h4 { font-family: var(--font-display); font-size: 1.3rem; color: var(--sage); margin-bottom: 12px; display:flex; gap:8px; align-items:center;}
  .hh-security ul { list-style: none; margin: 0; padding: 0; display: flex; gap: 16px; flex-wrap: wrap;}
  .hh-security li { font-size: 0.9rem; color: var(--ink); display: flex; align-items: center; gap: 8px; font-weight: 500;}
  
  .hh-warning {
    flex: 3; background: #FFF8E1; border: 1px solid #FFD54F;
    border-radius: var(--r-md); padding: 24px 32px;
  }
  .hh-dark .hh-warning { background: rgba(255, 213, 79, 0.05); border-color: rgba(255, 213, 79, 0.2); }
  .hh-warning h4 { color: #F57F17; font-size: 1rem; font-weight: 800; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.05em; display:flex; gap:8px; align-items:center;}
  .hh-warning p { font-size: 0.88rem; line-height: 1.6; color: var(--muted); margin: 0; }

  /* THE GREAT FOOTER */
  .hh-footer {
    flex-shrink: 0; background: var(--card); border-top: 1px solid var(--sand-light);
    position: relative; z-index: 1; padding: 40px 24px; margin-top: auto;
  }
  .hh-footer-container {
    max-width: 900px; margin: 0 auto;
    display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 32px;
  }
  
  .hh-f-brand { font-family: var(--font-display); font-size: 1.5rem; color: var(--ink); margin-bottom: 8px; }
  .hh-f-tagline { font-size: 0.85rem; color: var(--muted); margin-bottom: 16px; line-height: 1.5;}
  
  .hh-f-section-title {
    font-size: 0.72rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.1em; color: var(--muted); margin-bottom: 16px;
  }

  .hh-social-row { display: flex; gap: 12px; align-items: center; }
  .hh-social-link {
    display: flex; align-items: center; justify-content: center;
    width: 40px; height: 40px; border-radius: 50%;
    background: var(--bg-secondary, #F9FAFB); border: 1.5px solid var(--border-color, #E5E7EB);
    color: var(--text-secondary-color, #6B7280); font-size: 1.1rem;
    cursor: pointer; transition: var(--transition); text-decoration: none;
  }
  .hh-social-link:hover {
    background: var(--brand-wash); color: var(--brand);
    border-color: var(--brand); transform: translateY(-2px);
  }

  /* Contact Link */
  .hh-contact-link {
    display: flex; align-items: center; gap: 10px;
    font-size: 0.9rem; color: var(--text-color, #111);
    font-weight: 500; text-decoration: none; transition: var(--transition);
  }
  .hh-contact-link:hover { color: var(--brand); }
  
  .hh-copyright {
    max-width: 900px; margin: 32px auto 0;
    border-top: 1px solid var(--border-subtle, #f0f0f0); padding-top: 16px;
    text-align: center; font-size: 0.75rem; color: var(--text-muted, #9CA3AF);
  }

  .dashboard-container { overflow-x: hidden; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
`;

export default function AboutUs() {
  const navigate = useNavigate();

  const features = [
    { icon: "🤝", title: "Mentor Community", desc: "Real people offering emotional support, guidance, and understanding to help you through tough times." },
    { icon: "🤖", title: "Saksham AI", desc: "Your 24/7 friendly companion listening and responding with psychological insights — you’re never alone." },
    { icon: "💬", title: "Chattr", desc: "Safe, anonymous space to talk with others facing similar feelings — zero fear of judgment." },
    { icon: "🎧", title: "The Resource Hub", desc: "Easy breathing exercises, calming games, and soothing music to help you relax, reset, and feel more centered." },
    { icon: "🛤️", title: "Recovery Roadmaps", desc: "Science-based assessments helping you understand your journey and guiding your personal growth." },
    { icon: "📓", title: "Personal Journaling & Feed", desc: "Private Journal with weekly AI insights. Public Community Feed for shared growth when ready." },
    { icon: "👥", title: "Community Groups", desc: "Find your people. Openly talk about specific challenges with others who truly relate." },
    { icon: "✍️", title: "Story Write Section", desc: "Share your struggles and wins. Read inspiring stories reminding us that hope is always possible." }
  ];

  return (
    <>
      <style>{css}</style>
      <div className={`hh-root dashboard-container ${localStorage.getItem("theme") === "dark-theme" ? "hh-dark" : ""}`}>
        
        <main className="hh-main">
          
          <nav className="hh-top-nav">
            <button onClick={() => navigate(-1)} className="hh-back-btn">
              ← Back
            </button>
          </nav>

          <div className="hh-container">
            
            {/* PURE TYPOGRAPHY HERO SECTION */}
            <header className="hh-hero">
              <div className="hh-hero-badge">
                <span>✨</span> Welcome to your safe space
              </div>
              <h1 className="hh-title">
                About <span className="hh-title-highlight">HealHub</span>
              </h1>
              <div className="hh-tagline">Care that Clicks, Share what Heals.</div>
            </header>

            {/* INTRO TEXT */}
            <div className="hh-text-block" style={{ textAlign: "center", fontSize: "1.15rem" }}>
              <p><strong>You don’t have to carry everything alone.</strong></p>
              <p>
                Some days feel heavy — even when nothing big seems wrong. You might feel tired, 
                anxious, overwhelmed, or just “off” without knowing exactly why. It can be hard 
                to explain to others.
              </p>
              <p>If you’ve ever felt this way, we want you to know:</p>
              <p style={{ fontSize: "1.45rem", fontFamily: "var(--font-display)", color: "var(--brand)" }}>
                We see you. And it’s completely okay to not be okay.
              </p>
              <p>HealHub was created for moments just like these.</p>
            </div>

            {/* HIGHLIGHT BOX (What is HealHub) */}
            <div className="hh-highlight-box">
              <h3>🌱 What is HealHub?</h3>
              <p>
                HealHub is a caring and safe self-care platform built for your everyday well-being. 
                Whether you’re a student preparing for exams, a working professional feeling the pressure, 
                or anyone who just wants to feel a little lighter and calmer — we’re here for you. 
                We combine helpful self-care tools, real human connection, and smart AI support — all in one friendly place. 
                It’s designed to help you breathe easier, understand your feelings better, and grow at your own comfortable pace.
              </p>
            </div>

            {/* MISSION & VISION */}
            <div className="hh-split">
              <div className="hh-split-card">
                <h3>🎯 Our Mission</h3>
                <p>To make high-quality self-care tools and support available to everyone, no matter where they are.</p>
              </div>
              <div className="hh-split-card vision">
                <h3>🌅 Our Vision</h3>
                <p>To create a world where taking care of your daily well-being is normal, open, and free from shame or silence.</p>
              </div>
            </div>

            {/* THE "WHY" */}
            <div className="hh-text-block">
              <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.8rem", color: "var(--ink)", marginBottom: "16px" }}>💛 Why We Built HealHub</h3>
              <p>
                Healing is not always a straight line. It can feel lonely and confusing. We saw that many people needed one complete place that brings together everything helpful for their self-care journey:
              </p>
              <ul style={{ listStyleType: "none", paddingLeft: "16px", marginBottom: "20px", color: "var(--muted)" }}>
                <li style={{ marginBottom: "8px" }}>✨ Someone kind to talk to</li>
                <li style={{ marginBottom: "8px" }}>✨ Simple tools to feel better</li>
                <li style={{ marginBottom: "8px" }}>✨ A community that truly understands</li>
                <li style={{ marginBottom: "8px" }}>✨ Quick and safe guidance whenever needed</li>
              </ul>
              <p>
                So we created HealHub — a warm space where technology and genuine care come together. 
                Here, no one has to face difficult emotions all by themselves.
              </p>
            </div>

            {/* FEATURES GRID */}
            <h2 className="hh-section-title">🌐 Everything You’ll Find in HealHub</h2>
            <div className="hh-grid">
              {features.map((feat, i) => (
                <div className="hh-feature-card" key={i}>
                  <div className="hh-feature-icon">{feat.icon}</div>
                  <div className="hh-feature-title">{feat.title}</div>
                  <p className="hh-feature-desc">{feat.desc}</p>
                </div>
              ))}
            </div>

            {/* OUR STORY - Merged Origin Content */}
            <div className="hh-origin-story">
              <h2>📖 Our Story</h2>
              <div className="hh-origin-text-en">
                <p>
                  At HealHub, we believe that mental wellness deserves the same urgency, care, and 
                  innovation as any other aspect of health. As students of ABES Engineering College, 
                  we came together not just as coders and creators—but as empathetic individuals 
                  who’ve seen how stress, isolation, and emotional struggles affect those around us. 
                  HealHub was born from a shared vision: to build a safe, intelligent, and 
                  accessible space where technology meets compassion. It’s designed to be 
                  anonymous and protected with strong security.
                </p>
              </div>
              
            </div>

            {/* SECURITY & WARNING ROW */}
            <div className="hh-disclaimers-row">
              <div className="hh-security">
                <h4>🔒 Your Privacy is Sacred</h4>
                <ul>
                  <li>✓ Fully confidential</li>
                  <li>✓ Designed to be anonymous</li>
                  <li>✓ Protected with strong security</li>
                </ul>
              </div>

              <div className="hh-warning">
                <h4>⚠️ A Gentle Reminder</h4>
                <p>
                  HealHub supports your well-being but is NOT a replacement for professional medical help or emergency care. If you are in crisis, contact a licensed doctor, therapist, or emergency services in your area immediately.
                </p>
              </div>
            </div>

          </div>
        </main>

        {/* ═══════════════════════════════════════════════════════════════
           THE GREAT FOOTER
        ═══════════════════════════════════════════════════════════════ */}
        <footer className="hh-footer">
          <div className="hh-footer-container">
            
            {/* Branding Column */}
            <div className="hh-f-col">
              <div className="hh-f-brand">🌿 HealHub</div>
              <p className="hh-f-tagline">Care that clicks, share what heals.</p>
              <div className="nav-items" style={{ display: "flex", gap: "10px", fontSize: "0.8rem", flexWrap: "wrap"}}>
                <Link to="/">Home</Link>
                <Link to="/resources">Resources</Link>
                <Link to="/community">Community</Link>
              </div>
            </div>

            {/* Social Column - EMBEDDED LINKS */}
            <div className="hh-f-col">
              <div className="hh-f-section-title">Follow us on</div>
              <div className="hh-social-row">
                <a href="https://www.instagram.com/healhub.space/?utm_source=ig_web_button_share_sheet" target="_blank" rel="noopener noreferrer" className="hh-social-link" title="Follow us on Instagram">
                  <FaInstagram />
                </a>
                <a href="https://www.linkedin.com/company/healhubspace/" target="_blank" rel="noopener noreferrer" className="hh-social-link" title="Follow us on LinkedIn">
                  <FaLinkedinIn />
                </a>
                <a href="https://www.facebook.com/share/1LsPtXtNCC/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="hh-social-link" title="Follow us on Facebook">
                  <FaFacebookF />
                </a>
              </div>
            </div>

            {/* Contact Column - EMBEDDED EMAIL */}
            <div className="hh-f-col">
              <div className="hh-f-section-title">Contact us</div>
              <a href="mailto:healhubsupport@gmail.com" className="hh-contact-link">
                <FaEnvelope style={{ fontSize: "1.1rem", color: "var(--brand)" }} />
                healhubsupport@gmail.com
              </a>
              <p style={{fontSize:"0.75rem", color:"var(--muted)", marginTop:"8px", lineHeight:"1.4"}}>We usually respond within 24 hours.</p>
            </div>
            
          </div>
          
          <div className="hh-copyright">
            © {new Date().getFullYear()} HealHub. Built with ❤️ at ABES Engineering College. All rights reserved.
          </div>
        </footer>
      </div>
    </>
  );
}